import React, { useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { hasAiKey } from '../../lib/byok';
import { registerGateHost, isGateLeader, getGate, closeGate, giveConsent, setAiModePreference } from '../../lib/aiHosted';
import useAiAllowance from './useAiAllowance';
import AiConsentDialog from './AiConsentDialog';
import { AiLimitDialog, AiByokProDialog } from './AiUpgradeDialogs';
import AiPhiDialog from './AiPhiDialog';

let hostSeq = 0;

/**
 * Renders the hosted-AI dialogs (consent, limit reached, own key needs Pro) that api/ai.js
 * requests through lib/aiHosted's gate. Safe to mount in several places: only the first
 * mounted host renders. Mounted by AiPageHero, ExplainCodeButton, Settings and Account.
 */
export default function AiGateHost() {
  const idRef = useRef(null);
  if (idRef.current === null) {
    hostSeq += 1;
    idRef.current = hostSeq;
  }
  useEffect(() => registerGateHost(idRef.current), []);

  const ai = useAiAllowance();
  const { user, plan } = useAuth();
  const { toast } = useToast();
  if (!isGateLeader(idRef.current)) return null;

  const gate = getGate();
  const kind = gate ? gate.kind : null;
  const effectivePlan = (ai.data && ai.data.plan) || plan || 'guest';

  return (
    <>
      <AiConsentDialog
        open={kind === 'consent'}
        onAccept={() => {
          giveConsent();
          closeGate(true);
        }}
        onCancel={() => closeGate(false)}
      />
      <AiLimitDialog
        open={kind === 'limit'}
        onClose={() => closeGate(null)}
        details={gate && kind === 'limit' ? gate.payload : {}}
        credits={ai.credits}
        plan={effectivePlan}
        signedIn={!!user}
        hasOwnKey={ai.byokAllowed && hasAiKey()}
        onUseOwnKey={() => {
          setAiModePreference('byok');
          closeGate('byok');
          toast('Switched to your own API key. Run the request again to continue.');
        }}
      />
      <AiByokProDialog open={kind === 'byok-pro'} signedIn={!!user} onClose={() => closeGate(null)} onUseHosted={() => closeGate('hosted')} />
      <AiPhiDialog
        open={kind === 'phi'}
        matches={gate && kind === 'phi' ? gate.payload.matches : []}
        onEdit={() => closeGate('edit')}
        onConfirm={() => closeGate('confirm')}
      />
    </>
  );
}
